import { motion } from "framer-motion";
import { Languages, Mail, Phone, Star } from "lucide-react";
import ChapterHeading from "@/components/landing/ChapterHeading";

export default function Host() {
  return (
    <section id="host" data-testid="host-section" className="bg-[#EEF1E6] py-24 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeading eyebrow="Chi ti accoglie" title="L'Host — Alessio" subtitle="Host attento e affidabile, con particolare cura per dettagli, pulizia e comfort." />

        <div className="grid items-center gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4"
          >
            <div className="mx-auto flex max-w-xs flex-col items-center text-center">
              <img
                src="/photos/host2.webp"
                alt="Alessio, host di Saint Anthony, con il suo cagnolino"
                loading="lazy"
                className="h-44 w-44 rounded-full border-4 border-white object-cover shadow-xl shadow-stone-900/20"
              />
              <p className="mt-5 font-serif text-2xl text-stone-900">Alessio</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-stone-500">Il vostro host a Eggi</p>
              <div className="mt-4 flex items-center gap-2 rounded-full bg-white px-5 py-2.5 shadow-lg">
                <Star size={14} className="fill-oro text-oro" />
                <span className="text-sm font-semibold text-stone-800" data-testid="host-score">8,9 punteggio host</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-8"
          >
            <blockquote className="border-l-2 border-oro pl-6 font-serif text-xl italic leading-relaxed text-stone-700 sm:text-2xl">
              “Offro spazi funzionali e accoglienti, pensati per garantire un soggiorno semplice e
              piacevole. Sempre disponibile per supporto e consigli durante la permanenza.”
            </blockquote>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-stone-600">
              Il borgo di Eggi si trova a soli 6 km dalla splendida Spoleto, città ricca di storia,
              arte e monumenti. A 2 km dalla strada principale che collega comodamente Perugia, Assisi,
              Terni e la suggestiva Valnerina, la zona è ideale per chi ama la natura e le attività
              outdoor. I dintorni offrono numerosi ristoranti tipici dove gustare il meglio della
              cucina umbra.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                data-testid="host-phone-btn"
                href="tel:+393474860922"
                className="inline-flex items-center gap-2 rounded-full bg-olive px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-linen transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                <Phone size={15} /> 347 486 0922
              </a>
              <a
                data-testid="host-whatsapp-btn"
                href="https://wa.me/393474860922?text=Buongiorno%20Alessio%2C%20vorrei%20informazioni%20su%20Saint%20Anthony"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <span className="inline-flex items-center gap-2 text-sm text-stone-500">
                <Languages size={15} className="text-terracotta" /> Lingua parlata: Italiano
              </span>
              <a
                data-testid="host-email-link"
                href="mailto:saintanthony.spoleto@hotmail.com"
                className="inline-flex items-center gap-2 break-all text-sm text-stone-500 transition-colors hover:text-terracotta"
              >
                <Mail size={15} className="shrink-0 text-terracotta" /> saintanthony.spoleto@hotmail.com
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
